import { useEffect, useState } from "react";
import { SERVICE_OFFERING_DOCUMENT_KIND_LABELS } from "@/lib/service/offeringDocumentsClient";
import {
  formatQuotedAmount,
  getServiceRequestApprovalContext,
  queueOptions,
  type ServiceRequestApprovalContext,
  type ServiceRequestOperationalDraft,
} from "@/lib/service/requestApprovalClient";
import type { ServiceRequest, ServiceWorkQueue } from "@/lib/service/types";
import { ghostBtnClass, inputClass, primaryBtnClass, REQUEST_ORIGIN_LABELS, REQUEST_STATUS_LABELS } from "./serviceShared";

function DocumentLinkList(props: {
  title: string;
  empty: string;
  items: Array<{ id: string; label: string; href?: string }>;
}) {
  const { title, empty, items } = props;
  if (items.length === 0) {
    return (
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          {title}
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">{empty}</p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {title}
      </div>
      <ul className="mt-1 space-y-1">
        {items.map((item) => (
          <li key={item.id}>
            {item.href ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-indigo-700 underline underline-offset-2 hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-100"
              >
                {item.label}
              </a>
            ) : (
              <span className="text-sm text-gray-600 dark:text-zinc-400">{item.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ServiceRequestApprovalCard(props: {
  req: ServiceRequest;
  queues: ServiceWorkQueue[];
  offerings: Array<{ id: string; label: string }>;
  expanded: boolean;
  onToggle: () => void;
  draft: ServiceRequestOperationalDraft;
  reviewNotes: string;
  actionBusy: boolean;
  onDraftChange: (patch: Partial<ServiceRequestOperationalDraft>) => void;
  onReviewNotesChange: (value: string) => void;
  onAction: (action: "approve" | "decline" | "needs_info") => void;
}) {
  const {
    req,
    queues,
    offerings,
    expanded,
    onToggle,
    draft,
    reviewNotes,
    actionBusy,
    onDraftChange,
    onReviewNotesChange,
    onAction,
  } = props;

  const [context, setContext] = useState<ServiceRequestApprovalContext | null>(null);
  const [contextBusy, setContextBusy] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setContextBusy(true);
    setContextError(null);
    void getServiceRequestApprovalContext(req.id).then((res) => {
      if (cancelled) return;
      setContextBusy(false);
      if (!res.ok) {
        setContextError(res.error);
        setContext(null);
        return;
      }
      setContext({
        offering_label: res.data.offering_label,
        offering_documents: res.data.offering_documents,
        opportunity_documents: res.data.opportunity_documents,
        intake_attachments: res.data.intake_attachments,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [expanded, req.id, req.offering_id, req.opportunity_id]);

  const queueSelectOptions = queueOptions(queues, draft.queue_id);
  const offeringLabel =
    offerings.find((o) => o.id === req.offering_id)?.label ??
    (req.offering_id ? "Unknown service" : "No service");
  const summaryTitle =
    req.location_label || req.description.slice(0, 80) || "Service request";

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
            {summaryTitle}
            {req.origin === "opportunity_subscriber" && req.source_subscriber_org_name ? (
              <span className="ml-2 rounded-md bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
                {req.source_subscriber_org_name}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
            {offeringLabel} ·{" "}
            {queueSelectOptions.find((q) => q.id === req.queue_id)?.label ?? "Queue"} ·{" "}
            {req.job_kind === "internal" ? "Internal" : "Sold"} ·{" "}
            {REQUEST_ORIGIN_LABELS[req.origin] ?? req.origin} ·{" "}
            {REQUEST_STATUS_LABELS[req.status]}
          </div>
        </div>
        <span className="shrink-0 text-xs text-gray-400 dark:text-zinc-500" aria-hidden>
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-gray-100 px-4 py-4 dark:border-zinc-800">

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/40">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Quoted amount
          </div>
          <div className="mt-1 text-sm font-medium text-gray-900 dark:text-zinc-100">
            {formatQuotedAmount(req.quoted_amount_cents)}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
            Read-only. Revise the linked opportunity to change customer pricing.
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/40">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Offering
          </div>
          <div className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            {contextBusy ? "Loading…" : context?.offering_label ?? (req.offering_id ? "—" : "None")}
          </div>
        </div>
      </div>

      {req.scheduling_notes ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/20">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
            Scheduling notes
          </div>
          <p className="mt-1 text-sm text-amber-950 dark:text-amber-100 whitespace-pre-wrap">
            {req.scheduling_notes}
          </p>
        </div>
      ) : null}

      {contextError ? (
        <p className="text-sm text-red-700 dark:text-red-300">{contextError}</p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <DocumentLinkList
          title="Offering guidance"
          empty="No guidance documents for this offering."
          items={(context?.offering_documents ?? []).map((doc) => ({
            id: doc.id,
            label: `${SERVICE_OFFERING_DOCUMENT_KIND_LABELS[doc.kind]} — ${doc.filename}`,
            href: doc.signed_url,
          }))}
        />
        {req.opportunity_id ? (
          <DocumentLinkList
            title="Opportunity documents"
            empty={contextBusy ? "Loading…" : "No opportunity documents."}
            items={(context?.opportunity_documents ?? []).map((doc) => ({
              id: doc.id,
              label: doc.display_name?.trim() || doc.document_type || "Document",
              href: doc.signed_url,
            }))}
          />
        ) : null}
      </div>

      {(context?.intake_attachments?.length ?? 0) > 0 ? (
        <DocumentLinkList
          title="Intake photos"
          empty=""
          items={(context?.intake_attachments ?? []).map((att) => ({
            id: att.id,
            label: att.filename,
            href: att.signed_url,
          }))}
        />
      ) : null}

      <div className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Job type
        </label>
        <select
          className={inputClass}
          value={draft.queue_id ?? ""}
          onChange={(e) => onDraftChange({ queue_id: e.target.value || null })}
        >
          <option value="">Unassigned</option>
          {queueSelectOptions.map((q) => (
            <option key={q.id} value={q.id}>
              {q.label}
              {!q.is_active ? " (inactive)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Description / scope
        </label>
        <textarea
          className={`${inputClass} min-h-[72px]`}
          value={draft.description}
          onChange={(e) => onDraftChange({ description: e.target.value })}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Location label
          </label>
          <input
            className={inputClass}
            value={draft.location_label}
            onChange={(e) => onDraftChange({ location_label: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Location address
          </label>
          <input
            className={inputClass}
            value={draft.location_address}
            onChange={(e) => onDraftChange({ location_address: e.target.value })}
          />
        </div>
      </div>

      <textarea
        className={`${inputClass} min-h-[60px]`}
        placeholder="Review notes (required to send back)"
        value={reviewNotes}
        onChange={(e) => onReviewNotesChange(e.target.value)}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primaryBtnClass}
          disabled={actionBusy}
          onClick={() => onAction("approve")}
        >
          Approve
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={actionBusy}
          onClick={() => onAction("decline")}
        >
          Decline
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={actionBusy || !reviewNotes.trim()}
          onClick={() => onAction("needs_info")}
        >
          Send back
        </button>
      </div>
        </div>
      ) : null}
    </div>
  );
}
